import React from 'react'
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Portal,
  useBreakpointValue,
  usePrefersReducedMotion,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

export const gameZoneSlideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`

type GameZoneSheetProps = {
  children: React.ReactNode
  onClose: () => void
  isBusy?: boolean
  isClosable?: boolean
}

export const GameZoneSheet: React.FC<GameZoneSheetProps> = ({
  children,
  onClose,
  isBusy = false,
  isClosable = true,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const sheetStyles = {
    w: 'full',
    maxW: 'full',
    m: 0,
    p: 5,
    pb: 'calc(20px + env(safe-area-inset-bottom, 0px))',
    bg: 'ninja.600',
    color: 'white',
    borderRadius: '20px 20px 0 0',
    boxShadow: '0 20px 47px rgba(0, 0, 0, 0.25)',
    overflowY: 'auto' as const,
    overscrollBehavior: 'contain' as const,
  }

  return (
    <Modal
      isOpen
      portalProps={{ appendToParentPortal: false }}
      onClose={onClose}
      closeOnEsc={isClosable && !isBusy}
      closeOnOverlayClick={isClosable && !isBusy}
      motionPreset={prefersReducedMotion ? 'none' : 'slideInBottom'}
    >
      <ModalOverlay bg="rgba(19, 19, 49, 0.9)" />
      <ModalContent {...sheetStyles} maxH="100dvh" containerProps={{ alignItems: 'flex-end' }}>
        <ModalBody p={0}>{children}</ModalBody>
      </ModalContent>
    </Modal>
  )
}

// Keep desktop overlays inside the game, but mount mobile overlays at the page root.
export const GameZoneOverlayPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  return isMobile ? <Portal appendToParentPortal={false}>{children}</Portal> : <>{children}</>
}
